import React, { useEffect, useState } from 'react'
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import HomeVideo2 from '../../../assets/images/banner/banner-1.mp4';
import FooterContact from '../../../component/FooterContact';

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } }
};

const pillars = [
    {
        icon: (
            <svg width="40" height="40" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25 2L4 12V26C4 37.1 13.3 47.5 25 50C36.7 47.5 46 37.1 46 26V12L25 2Z" stroke="#2458f3" strokeWidth="2" fill="none" />
                <path d="M16 25L22 31L34 19" stroke="#2458f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        title: 'Identity Verification',
        desc: 'Continuously verify every user and device attempting to access your network, regardless of location.'
    },
    {
        icon: (
            <svg width="40" height="40" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="20" width="34" height="26" rx="3" stroke="#2458f3" strokeWidth="2" fill="none" />
                <path d="M17 20V14C17 9 33 9 33 14V20" stroke="#2458f3" strokeWidth="2" strokeLinecap="round" />
                <circle cx="25" cy="33" r="3" fill="#2458f3" />
            </svg>
        ),
        title: 'Least Privilege Access',
        desc: 'Grant minimal access rights needed for users to perform their functions, reducing the attack surface.'
    },
    {
        icon: (
            <svg width="40" height="40" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="25" cy="25" r="20" stroke="#2458f3" strokeWidth="2" fill="none" />
                <path d="M15 25H35M25 15V35" stroke="#2458f3" strokeWidth="2" strokeLinecap="round" />
                <circle cx="25" cy="25" r="8" stroke="#2458f3" strokeWidth="1.5" fill="none" />
            </svg>
        ),
        title: 'Micro-Segmentation',
        desc: 'Divide your network into secure zones to contain breaches and prevent lateral movement by attackers.'
    },
    {
        icon: (
            <svg width="40" height="40" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 10L40 40M40 10L10 40" stroke="#2458f3" strokeWidth="2" strokeLinecap="round" />
                <rect x="5" y="5" width="40" height="40" rx="5" stroke="#2458f3" strokeWidth="2" fill="none" />
            </svg>
        ),
        title: 'Assume Breach',
        desc: 'Operate under the assumption that a breach has occurred to minimize damage and respond effectively.'
    }
];

const steps = [
    { number: '01', title: 'Identify Assets', desc: 'Map all users, devices, applications, and data flows across your organization.' },
    { number: '02', title: 'Define Policies', desc: 'Establish granular access control policies based on identity, device posture, and context.' },
    { number: '03', title: 'Enforce Controls', desc: 'Deploy multi-factor authentication, encryption, and microsegmentation across your environment.' },
    { number: '04', title: 'Monitor & Adapt', desc: 'Continuously monitor all traffic, validate trust signals, and refine policies based on new threats.' }
];

export default function ZeroTrust() {

    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        const interval = setInterval(() => {
            setActiveStep(prev => (prev + 1) % steps.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <>
            {/* Hero Banner */}
            <section className="tech-hero-banner" style={{ position: 'relative', overflow: 'hidden' }}>
                <div className="tech-hero-overlay"></div>
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center" style={{ minHeight: '85vh' }}>
                        <div className="col-lg-7 col-md-12">
                            <motion.div
                                className="tech-hero-content"
                                initial="hidden"
                                animate="visible"
                                variants={staggerContainer}
                            >
                                <motion.div variants={fadeUp} className="tech-breadcrumb">
                                    <Link to="/">Home</Link>
                                    <span className="tech-breadcrumb-sep"> / </span>
                                    <span>Zero Trust</span>
                                </motion.div>
                                <motion.div variants={fadeUp} className="tech-badge">
                                    Next-Gen Security Framework
                                </motion.div>
                                <motion.h1 variants={fadeUp} className="tech-hero-title">
                                    Zero Trust<br />
                                    <span className="tech-hero-gradient">Network Access</span>
                                </motion.h1>
                                <motion.p variants={fadeUp} className="tech-hero-desc">
                                    Traditional security perimeters no longer suffice. Zero Trust redefines protection by eliminating implicit trust - every user, device, and connection is verified continuously.
                                </motion.p>
                                <motion.div variants={fadeUp} className="tech-hero-actions">
                                    <Link to="/contact" className="tech-btn-primary">Get Protected Now</Link>
                                    <Link to="/contact" className="tech-btn-ghost">Learn More</Link>
                                </motion.div>
                            </motion.div>
                        </div>
                        <div className="col-lg-5 col-md-12">
                            <motion.div
                                className="tech-hero-visual"
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.9, delay: 0.3 }}
                            >
                                <div className="zt-shield-visual">
                                    <div className="zt-shield-ring zt-ring-1"></div>
                                    <div className="zt-shield-ring zt-ring-2"></div>
                                    <div className="zt-shield-ring zt-ring-3"></div>
                                    <div className="zt-shield-core">
                                        <svg width="60" height="60" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M25 2L4 12V26C4 37.1 13.3 47.5 25 50C36.7 47.5 46 37.1 46 26V12L25 2Z" fill="rgba(36,88,243,0.3)" stroke="#2458f3" strokeWidth="1.5" />
                                            <path d="M16 25L22 31L34 19" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div className="zt-orbit-node zt-node-1">
                                        <span>ID</span>
                                    </div>
                                    <div className="zt-orbit-node zt-node-2">
                                        <span>MFA</span>
                                    </div>
                                    <div className="zt-orbit-node zt-node-3">
                                        <span>AI</span>
                                    </div>
                                    <div className="zt-orbit-node zt-node-4">
                                        <span>EDR</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <video autoPlay loop muted style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
                    <source src={HomeVideo2} type="video/mp4" />
                </video>
            </section>

            {/* What is Zero Trust */}
            <section className="tech-section-light">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag">Understanding Zero Trust</div>
                            <h2 className="tech-section-heading">What Is <span className="tech-text-accent">Zero Trust?</span></h2>
                            <p className="tech-section-para">
                                Zero Trust is a security framework requiring all users - whether inside or outside the organization's network - to be continuously authenticated, authorized, and validated before accessing applications and data.
                            </p>
                            <p className="tech-section-para">
                                Unlike traditional perimeter security, Zero Trust assumes no user or system is inherently trustworthy. It embraces the principle: <strong>"Never trust, always verify."</strong>
                            </p>
                            <div className="tech-quote-block">
                                <p>"Zero Trust is a significant departure from the 'trust but verify' method - it uniquely addresses modern challenges including remote workers, hybrid cloud environments, and ransomware threats."</p>
                            </div>
                            <Link to="/contact" className="tech-btn-primary mt-4 d-inline-block">Talk To Our Experts</Link>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/zero-1.webp')} alt='Zero Trust' />
                                <div className="tech-image-badge">
                                    <span className="badge-num">100%</span>
                                    <span className="badge-label">Verified Access</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Four Pillars */}
            <section className="tech-section-dark">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag light">Core Pillars</div>
                        <h2 className="tech-section-heading text-white">The Four Pillars of <span className="tech-text-accent">Zero Trust</span></h2>
                        <p className="tech-section-para text-white" style={{ maxWidth: 600, margin: '0 auto' }}>
                            A complete Zero Trust implementation is built on these fundamental principles that work together to create an uncompromising security posture.
                        </p>
                    </motion.div>
                    <motion.div
                        className="row mt-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {pillars.map((pillar, i) => (
                            <motion.div className="col-lg-3 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-pillar-card">
                                    <div className="tech-pillar-icon">{pillar.icon}</div>
                                    <h3 className="tech-pillar-title">{pillar.title}</h3>
                                    <p className="tech-pillar-desc">{pillar.desc}</p>
                                    <div className="tech-pillar-line"></div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* How It Works */}
            <section className="tech-section-light">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/zerot-trust-2.webp')} alt='How Zero Trust Works' />
                            </div>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag">The Process</div>
                            <h2 className="tech-section-heading">How Zero Trust <span className="tech-text-accent">Works</span></h2>
                            <p className="tech-section-para">
                                Execution combines advanced technologies such as risk-based multi-factor authentication, identity protection, next-generation endpoint security, and robust cloud workload technology.
                            </p>
                            <div className="tech-steps-list">
                                {steps.map((step, i) => (
                                    <div
                                        key={i}
                                        className={`tech-step-item ${activeStep === i ? 'active' : ''}`}
                                        onClick={() => setActiveStep(i)}
                                    >
                                        <div className="tech-step-number">{step.number}</div>
                                        <div className="tech-step-content">
                                            <h4>{step.title}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Banner */}
            <section className="tech-stats-section">
                <div className="container">
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {[
                            { stat: '85%', label: 'Reduction in breach risk' },
                            { stat: '3x', label: 'Faster threat detection' },
                            { stat: '99.9%', label: 'Uptime with Zero Trust' },
                            { stat: '60%', label: 'Lower security costs' }
                        ].map((item, i) => (
                            <motion.div className="col-lg-3 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-stat-card">
                                    <div className="tech-stat-num">{item.stat}</div>
                                    <div className="tech-stat-label">{item.label}</div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <FooterContact />
        </>
    )
}
